import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { importRowReason } from '../../utils/supplyAliasCopy';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { toast } from '@/hooks/use-toast';
import { trpc } from '@/lib/trpc';
import { useInventoryQueries } from '../../hooks/useInventoryQueries';
import { useConnectedClients } from '../../hooks/useConnectedClients';
import { ClientTargetPicker } from '../ClientTargetPicker';
import BulkUploadErrorBoundary from '@/components/csv-import-wizard/BulkUploadErrorBoundary';
import StepIndicator from '@/components/csv-import-wizard/StepIndicator';
import UploadStep from './steps/UploadStep';
import ValidationStep from './steps/ValidationStep';
import PreviewStep from './steps/PreviewStep';
import ProcessingStep from './steps/ProcessingStep';
import ResultStep from './steps/ResultStep';
import { useBulkStockUpload } from './useBulkStockUpload';
import { filterToTargetOrg } from '../../utils/filterToTargetOrg';

const SHARED_LOCATIONS_ENABLED =
  (import.meta.env.VITE_SHARED_LOCATIONS_ENABLED ?? '') === 'true';

const ADD_STOCK_STEPS = [
  { key: 'upload', label: 'Upload' },
  { key: 'validation', label: 'Validate' },
  { key: 'preview', label: 'Preview' },
  { key: 'processing', label: 'Process' },
  { key: 'result', label: 'Done' },
];

interface BulkStockUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const BulkStockUploadModal: React.FC<BulkStockUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { selectedLocation } = useLocation();
  const { organization } = useOrganization();
  // BLU-1189: gated on `isOpen`. The modal is mounted by the Inventory page
  // regardless of state, and its key carries no production-type filter, so an
  // ungated catalogue here is a second full fetch the moment the operator
  // filters the page. The preview reads `currentStock` / `isNewAtLocation`
  // off `products`, so the step content below waits for the catalogue rather
  // than calling every existing SKU new at this location.
  const { products, loading: productsLoading } = useInventoryQueries(undefined, {
    enabled: isOpen,
  });
  const store = useBulkStockUpload();
  const utils = trpc.useUtils();
  const { t } = useTranslation('inventory');

  // BLU-3xx Phase 47.01.2-B-fix3.5 (BUG 3): cross-org target picker so
  // operators can bulk-import a client's catalog. Mirrors AddStockModal —
  // required at shared 3PL locations with active client junctions, hidden
  // elsewhere. Backend already accepts targetOrganizationId on
  // bulkCsvImport (B-06) and resolves SKUs against the picked client's
  // catalog; this connects the frontend.
  const [targetOrgId, setTargetOrgId] = useState<string | null>(null);

  // Reset picker on close AND on location change — fresh-pick each time so
  // muscle-memory misroutes can't slip through (BLU-669: previously only
  // reset on close, diverging from the deliberate rule the other three
  // cross-org modals implement).
  useEffect(() => {
    if (!isOpen) setTargetOrgId(null);
  }, [isOpen]);
  useEffect(() => {
    setTargetOrgId(null);
  }, [selectedLocation?.id]);

  const { clients: connectedClients } = useConnectedClients({
    locationId: selectedLocation?.id,
    enabled: isOpen && SHARED_LOCATIONS_ENABLED && !!organization?.id,
  });

  const showPicker =
    SHARED_LOCATIONS_ENABLED &&
    !!organization?.id &&
    !!selectedLocation?.id &&
    connectedClients.length > 0;

  // Pass targetOrgId to listSkus so the org-wide SKU list reflects the
  // picked client's catalog (cross-org listSkus is auth-gated server-side
  // via gateTargetOrganization). Falls back to caller-org when no target.
  const allSkusQuery = trpc.inventory.listSkus.useQuery(
    targetOrgId && targetOrgId !== organization?.id && selectedLocation?.id
      ? { targetOrganizationId: targetOrgId, locationId: selectedLocation.id }
      : {},
    { staleTime: 60_000, enabled: isOpen },
  );
  const allSkus = allSkusQuery.data ?? [];
  const catalogueLoading = productsLoading || allSkusQuery.isLoading;

  // -----------------------------------------------------------------------
  // Mutations
  // -----------------------------------------------------------------------
  const bulkCsvImportMutation = trpc.inventory.bulkCsvImport.useMutation();
  const undoBulkImportMutation = trpc.inventory.undoBulkImport.useMutation();

  // Reset store on unmount to prevent stale state
  useEffect(() => () => { useBulkStockUpload.getState().reset(); }, []);

  // -----------------------------------------------------------------------
  // Submit handler — triggered from PreviewStep
  // -----------------------------------------------------------------------
  const handleSubmit = useCallback(async () => {
    if (!selectedLocation) return;
    // BLU-669: the picker's `required` was decorative here — nothing gated the
    // wizard, so a full CSV could submit with no target (implicit own-org)
    // against a cross-org-inclusive product list. Hard gate, mirroring
    // AddStockModal's handleSubmit.
    if (showPicker && !targetOrgId) {
      toast({
        title: t('crossOrg.bulk.targetRequiredTitle'),
        description: t('crossOrg.bulk.targetRequiredBody'),
        variant: 'destructive',
      });
      return;
    }

    const { previewRows, file, blockOnErrors } = store;

    // Prepare rows for the server
    const rows = previewRows.map((r) => ({
      sku: r.sku,
      quantity: r.qtyToAdd,
    }));

    store.startProcessing(rows.length);

    try {
      const result = await bulkCsvImportMutation.mutateAsync({
        locationId: selectedLocation.id,
        filename: file?.name,
        // Operator reference for the adjustment-document header.
        ...(store.reference.trim() ? { reference: store.reference.trim() } : {}),
        blockOnErrors,
        rows,
        // BLU-3xx Phase 47.01.2-B-fix3.5 (BUG 3): cross-org target. When
        // set, backend resolves SKUs against the picked client's catalog
        // (B-06 widening). Omit when own-org so single-tenant byte-equiv.
        ...(targetOrgId && targetOrgId !== organization?.id
          ? { targetOrganizationId: targetOrgId }
          : {}),
      });

      // Invalidate inventory queries so stock levels refresh
      void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });

      // Normalize execution errors from server format { sku, reason } to strings
      // Phase 92: a `supply_alias` row carries the translated reason.
      const execErrors = (result.executionErrors ?? []).map(
        (e: string | { sku: string; reason: string; reasonCode?: string }) =>
          typeof e === 'string' ? e : `${e.sku}: ${importRowReason(t, e)}`
      );

      if (result.success) {
        // BLU-1268: rows on a Just in time pair moved nothing; the server
        // counts them apart from the successes, and so do the toast and result.
        const skippedJustInTime = result.skippedJustInTime ?? 0;
        const skippedNote = skippedJustInTime > 0 ? t('supply.bulkSkipped', { count: skippedJustInTime }) : null;
        store.setResult({
          batchId: result.batchId ?? '',
          successCount: result.successCount,
          failedCount: result.failedCount,
          validationErrors: result.validationErrors ?? [],
          executionErrors: execErrors,
          mode: result.mode,
          skippedJustInTime,
        });

        if (result.failedCount === 0) {
          toast({
            title: 'Bulk upload complete',
            description: [`Added stock to ${result.successCount} items.`, skippedNote].filter(Boolean).join(' '),
          });
          onSuccess();
        } else {
          toast({
            title: 'Bulk upload completed with errors',
            description: [`${result.successCount} succeeded, ${result.failedCount} failed.`, skippedNote]
              .filter(Boolean)
              .join(' '),
            variant: 'destructive',
          });
        }
      } else {
        // Blocked by validation errors from the server
        store.setResult({
          batchId: '',
          successCount: 0,
          failedCount: result.failedCount,
          validationErrors: result.validationErrors ?? [],
          executionErrors: execErrors,
          mode: 'blocked',
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      const isNetworkError =
        message.includes('Failed to fetch') ||
        message.includes('NetworkError') ||
        message.includes('timeout') ||
        message.includes('ECONNREFUSED');

      store.setResult({
        batchId: '',
        successCount: 0,
        failedCount: previewRows.length,
        validationErrors: [],
        executionErrors: [message],
        mode: isNetworkError ? 'network_error' : undefined,
      });

      toast({
        title: isNetworkError ? 'Connection lost' : 'Bulk upload failed',
        description: message,
        variant: 'destructive',
      });
    }
  }, [
    selectedLocation,
    store,
    bulkCsvImportMutation,
    utils.inventory.getProducts,
    onSuccess,
    targetOrgId,
    organization?.id,
    showPicker,
    t,
  ]);

  // -----------------------------------------------------------------------
  // Close handler
  // -----------------------------------------------------------------------
  const handleClose = useCallback(() => {
    store.reset();
    onClose();
  }, [store, onClose]);

  // -----------------------------------------------------------------------
  // Upload another — reset everything
  // -----------------------------------------------------------------------
  const handleUploadAnother = useCallback(() => {
    store.reset();
  }, [store]);

  // -----------------------------------------------------------------------
  // Go back to validation step (from blocked result)
  // -----------------------------------------------------------------------
  const handleGoBack = useCallback(() => {
    store.clearResult();
    store.setStep('validation');
  }, [store]);

  // -----------------------------------------------------------------------
  // Undo handler — triggered from ResultStep
  // -----------------------------------------------------------------------
  const handleUndo = useCallback(async () => {
    if (!selectedLocation || !store.result?.batchId) return;

    try {
      await undoBulkImportMutation.mutateAsync({
        locationId: selectedLocation.id,
        batchId: store.result.batchId,
      });

      void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });

      toast({
        title: 'Undo complete',
        description: 'All stock changes from this upload have been reversed.',
      });

      onSuccess();
      handleClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast({
        title: 'Undo failed',
        description: message,
        variant: 'destructive',
      });
    }
  }, [selectedLocation, store.result, undoBulkImportMutation, utils, onSuccess, handleClose]);

  // -----------------------------------------------------------------------
  // Prevent closing during processing
  // -----------------------------------------------------------------------
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && store.step !== 'processing') {
        handleClose();
      }
    },
    [store.step, handleClose]
  );

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <BulkUploadErrorBoundary onReset={() => store.reset()}>
          <DialogHeader>
            <DialogTitle>Bulk Add Stock</DialogTitle>
            <DialogDescription>
              Upload a CSV file with SKUs and stock quantities to add
            </DialogDescription>
          </DialogHeader>

          {/* BLU-3xx Phase 47.01.2-B-fix3.5 (BUG 3): cross-org target picker.
              Sits at the top of the wizard so the operator picks the target
              client BEFORE uploading, mirroring AddStockModal. The org-wide
              SKU lookup downstream uses this to resolve cross-org SKU
              strings against the picked client's catalog (B-06 widening). */}
          {showPicker && organization && (
            <div className="rounded-md border border-border/60 bg-muted/20 p-3">
              <ClientTargetPicker
                value={targetOrgId}
                onChange={setTargetOrgId}
                ownOrgId={organization.id}
                ownOrgName={organization.name ?? t('crossOrg.picker.yourOrgFallback')}
                locationId={selectedLocation?.id ?? ''}
                required
              />
            </div>
          )}

          {/* Step indicator */}
          <div className="py-2">
            <StepIndicator
              steps={ADD_STOCK_STEPS}
              currentStep={store.step}
            />
          </div>

          {/* Step content */}
          <div className="min-h-[200px]">
            {catalogueLoading ? (
              <div className="flex flex-col items-center justify-center gap-3 py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
                <p className="text-sm text-muted-foreground">
                  {t('bulkStockUpload.loadingProducts')}
                </p>
              </div>
            ) : (
              <>
                {store.step === 'upload' && <UploadStep />}

                {store.step === 'validation' && (
                  // BLU-669: narrow the cross-org-inclusive product list to the
                  // picked target — a CSV of a CLIENT's SKUs must not preview
                  // clean under an own-org target. (Unset target passes through
                  // unfiltered; the submit gate + disabled preview button are the
                  // guard for that state.)
                  <ValidationStep
                    products={filterToTargetOrg(products, targetOrgId, organization?.id)}
                    allSkus={allSkus}
                  />
                )}

                {store.step === 'preview' && (
                  <PreviewStep
                    onSubmit={handleSubmit}
                    submitDisabledReason={
                      showPicker && !targetOrgId ? t('crossOrg.bulk.targetRequiredBody') : null
                    }
                  />
                )}

                {store.step === 'processing' && <ProcessingStep />}

                {store.step === 'result' && (
                  <ResultStep
                    onUploadAnother={handleUploadAnother}
                    onUndo={handleUndo}
                    isUndoing={undoBulkImportMutation.isPending}
                    onClose={handleClose}
                    onGoBack={handleGoBack}
                  />
                )}
              </>
            )}
          </div>
        </BulkUploadErrorBoundary>
      </DialogContent>
    </Dialog>
  );
};

export default BulkStockUploadModal;
